import React, { useCallback, useMemo } from 'react';
import {
  useTable as useReactTable,
  useSortBy,
  usePagination,
  useExpanded,
} from 'react-table';
import cn from 'classnames';
import Body from './Body';
import Columns from './Columns';
import Header from './Header';
import Row from './Row';

/**
 * @param {data} Set the data from the server
 * @param {headers} The column header to be set for the table
 * @param {isManualSorting} set to True to set your custom sorting
 * @param {isRowExpandable} set to True to enable the expandable functionality
 * @param {initialState} react table initialState
 * @param {useControlledState} Manually controlls table state
 * @returns {}
 */
const useTable = ({
  data,
  headers,
  isManualSorting = false,
  isRowExpandable = false,
  hasPagination = false,
  autoResetPage = true, // reset pagination if data changes
  useControlledState,
  initialState = {},
  dependeciesForCalculateHeaders = [],
  aditionalHeaderRows = null,
}) => {
  const rowData = React.useMemo(() => data, [data]);
  // we cannot put headers in the dependency array because it will cause an infinite loop
  // we cannot put data in the dependency array because it will cause an infinite loop
  // so we need to pass a custom dependency array to calculate the headers
  const columnHeaders = React.useMemo(
    () => headers,
    dependeciesForCalculateHeaders,
  );

  const hooks = [useSortBy];
  const extraOptions = {};

  if (useControlledState) {
    extraOptions.useControlledState = useControlledState;
  }

  if (isRowExpandable) {
    hooks.push(useExpanded);
    extraOptions.paginateExpandedRows = false;
  }

  if (hasPagination) {
    hooks.push(usePagination);
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,

    // pagination props
    page, // with pagination use page instead of rows
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    prepareRow,
    toggleSortBy,
    state: { sortBy, pageIndex, pageSize },
  } = useReactTable(
    {
      columns: columnHeaders,
      data: rowData,
      manualSortBy: isManualSorting,
      autoResetSortBy: isManualSorting,
      autoResetPage,
      initialState,
      defaultCanSort: isManualSorting,
      ...extraOptions,
    },
    ...hooks,
  );
  const rawTableItems = hasPagination ? page : rows;
  const tableItems = useMemo(
    () =>
      rawTableItems.map((row) => {
        prepareRow(row);
        return row;
      }),
    [rawTableItems],
  );

  const Table = useCallback(
    ({ children, className, includeDefaultRoleProp = true }) => {
      const defaultTableProps = getTableProps();

      if (
        Object.keys(defaultTableProps).indexOf('role') !== -1 &&
        !includeDefaultRoleProp
      ) {
        delete defaultTableProps.role;
      }

      return (
        <table
          className={cn(`${className} border-collapse`, {
            'w-full': className && !className.includes('w-0'),
          })}
          {...defaultTableProps}
        >
          {children}
        </table>
      );
    },
    [],
  );

  const TableBody = useCallback(
    ({ children, includeDefaultRoleProp = true, ...extraProps }) => {
      const defaultTableBodyProps = getTableBodyProps;

      if (
        Object.keys(defaultTableBodyProps).indexOf('role') !== -1 &&
        !includeDefaultRoleProp
      ) {
        delete defaultTableBodyProps.role;
      }

      const getCustomTableBodyProps = () => defaultTableBodyProps;

      return (
        <Body
          getTableBodyProps={getCustomTableBodyProps}
          {...defaultTableBodyProps}
          {...extraProps}
        >
          {children}
        </Body>
      );
    },
    [],
  );

  const TableHeader = useCallback(
    ({ children, ...extraProps }) => (
      <Header
        headerGroups={headerGroups}
        aditionalHeaderRows={aditionalHeaderRows}
        {...extraProps}
      >
        {children}
      </Header>
    ),
    [headers],
  );

  return {
    Table,
    TableHeader,
    TableBody,
    TableRow: Row,
    TableColumns: Columns,
    toggleSortBy,
    tableItems,
    sortBy,

    // pagination props
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageIndex,
    pageSize,
  };
};

export default useTable;
